import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["new", "contacted", "qualified", "won", "lost"];
const STAGE_COLOR = { new: "bg-blue-500/10 text-blue-300", contacted: "bg-cyan-500/10 text-cyan-300", qualified: "bg-violet-500/10 text-violet-300", won: "bg-emerald-500/10 text-emerald-300", lost: "bg-rose-500/10 text-rose-300" };

const empty = { name: "", email: "", phone: "", source: "", stage: "new", value: 0, notes: "" };

export default function Leads() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/leads");
      setItems(data);
    } catch (error) {
      console.error("Failed to load leads:", error);
      toast.error("Failed to load leads");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => (payload[k] === "" || payload[k] == null) && delete payload[k]);
      payload.value = Number(payload.value || 0);
      await api.post("/crm/leads", payload);
      toast.success(t("common.create")); setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const setStage = async (lid, stage) => {
    try { await api.patch(`/crm/leads/${lid}`, { name: items.find(x => x.lead_id === lid)?.name, stage }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <DashboardLayout title={t("crm.leads_title")} subtitle={t("crm.leads_subtitle")}
      right={<button data-testid="leads-new" onClick={() => setOpen(true)} className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white font-medium text-sm flex items-center gap-2 hover:bg-blue-400"><Plus className="w-4 h-4" /> New lead</button>}>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="leads-board">
        {STAGES.map((s) => (
          <div key={s} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
            <div className="flex items-center justify-between mb-3">
              <div className={`text-xs px-2 py-0.5 rounded ${STAGE_COLOR[s]}`}>{s}</div>
              <div className="text-xs text-zinc-500">{items.filter(i => i.stage === s).length}</div>
            </div>
            <div className="space-y-2 min-h-[100px]">
              {items.filter(i => i.stage === s).map((l) => (
                <div key={l.lead_id} className="rounded-md border border-zinc-800 bg-zinc-950 p-3 text-sm hover:border-blue-500/40 transition-colors" data-testid={`lead-card-${l.lead_id}`}>
                  <Link to={`/app/leads/${l.lead_id}`} className="text-zinc-100 hover:text-blue-300" data-testid={`lead-open-${l.lead_id}`}>{l.name}</Link>
                  <div className="text-xs text-zinc-500">{l.email || l.phone || "—"}</div>
                  {l.service_needed && <div className="text-xs text-zinc-400 mt-0.5 truncate">{l.service_needed}</div>}
                  <div className="text-xs text-blue-400 mt-1">${Number(l.value || 0).toFixed(0)}</div>
                  <select value={l.stage} onChange={(e) => setStage(l.lead_id, e.target.value)} data-testid={`lead-stage-${l.lead_id}`} className="mt-2 w-full h-7 bg-zinc-900 border border-zinc-800 rounded text-[11px] px-1">
                    {STAGES.map(ss => <option key={ss} value={ss}>{ss}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="lead-dialog">
          <DialogHeader><DialogTitle>New lead</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {[["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["source", "Source"], ["value", "Value ($)"]].map(([k, l]) => (
              <label key={k}><span className="overline block mb-1.5">{l}</span>
                <input data-testid={`lead-field-${k}`} type={k === "value" ? "number" : "text"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
              </label>
            ))}
            <label><span className="overline block mb-1.5">Stage</span>
              <select data-testid="lead-field-stage" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
            <button data-testid="lead-save" onClick={submit} className="btn-scale h-9 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400">{t("common.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
